<template>
  <PgTable
    class="pg-page-edit-table"
    ref="pgTable"
    v-bind="$attrs"
    :loading="loading"
    :showOverflow="showOverflow"
    :extendCompileData="tableCompile"
    :rowConfig="rowConfig"
    :dataParameters="dataParameters"
    :column="columns"
    :seachMethod="seachMethod"
    :sortConfig="{ defaultSort: tableSortings }"
    @size-change="getDataAsyn(1)"
    @current-change="getDataAsyn()"
    @checkbox-change="checkboxChange"
    @checkbox-all="checkboxAll"
    @sort-change="sortChangeFn"
    @column-custom="columnCustom"
  >
    <template #right>
      <template v-for="(item, index) in rightComponents ?? []">
        <component
          :is="item.name"
          v-bind="item.props"
          @input="(val) => onInput(item, val)"
          :key="index"
        ></component>
      </template>
    </template>
    <template #tableTop>
      <PgQueryTab
        class="pg-page-edit-table__query-tab"
        :queryTabs="queryTabList"
        :data="queryData"
        :total="totalData"
        @change="getDataAsyn(1)"
      ></PgQueryTab>
    </template>
  </PgTable>
</template>

<script>
export default {
  inheritAttrs: false,
}
</script>
<script setup>
import { PgTable } from '../../table/index'
import { inject, ref, onMounted, computed } from 'vue'
import { FILED_NOT_WATCH, PAGE_EDIT_SCROLL_INJECTION_KEY } from '../../../tokens/index'
import { getElementTop, post, setValueByPath, getValueByPath } from '../../../utils/index'
import { useTableSort } from '../../../hooks/index'
import { PgQueryTab } from '../../page-query-top-search/index'
import { BUTTONGROUP } from '../../../tokens/index'

const props = defineProps({
  extendCompileData: {
    type: Object,
  },
  fixedElement: {
    type: Array,
    default: () => [],
  },
  isFloat: Boolean,
  rowConfig: {
    type: Object,
    default: null,
  },
  //如果配置了url则表格数据会通过接口获取
  url: String,
  dataParameters: Object,
  // 仅在配置url时有效，将接口返回的字段赋值到data中, 当存在resDataKey是则分页使用前端分页
  resDataKey: String,
  columnTableCode: String,
  columnUrl: {
    type: String,
    default: 'rest/ezcomp/dict/table/fetch/',
  },
  //配合标签懒加载使用，使用标签懒加载时将该属性配置为true，表格渲染完成后就会显示表格数据
  initLoad: Boolean,
  showOverflow: {
    type: [Boolean, String],
    default: true,
  },
  value: [String, Array, Object, Boolean, Number],
  rightComponents: Array,
  // 表格上方的查询tab 格式如:
  // [{
  //  "transform": "是否对对象类型进行转换，支持结构：{"url": ”请求接口", "tableCode": "表格code", "value": "对应property的值"}",
  //  "property": "aaa(传给后端的key)",
  //  "parameter": {
  //  "options": [{
  //  "name": "全部(支持显示合计数量{{'待办（' + ($.getValue(total, 'count_status') || 0) + '）'}}")",
  //  "value": "",
  //  "alias":"与按钮中配置的按钮code一致（只有该属性存在时才会进行权限过滤，配置别名时不允许设置默认值）" }
  // ]}
  //}]
  queryTabs: Array,
  queryTabsButton: String,
})

const emit = defineEmits(['input'])
const onInput = (item, val) => {
  emit('input', { item, val })
}

const pgTable = ref(null)
const { setData, init, onSelections, onSeachParameters, loadTable } = inject(FILED_NOT_WATCH, {})
const loading = ref(false)
const totalData = ref(null)
const queryData = ref({})
const tableCode = ref(props.columnTableCode)

const { columns, getcolumns, sortChange, reloadColumn, tableSortings, sortings } = useTableSort({
  columnUrl: props.columnUrl,
  columnTableCode: () => tableCode.value,
  isSession: false,
})

const tableCompile = computed(() => {
  return { queryData: queryData.value, ...props.extendCompileData }
})

const { getButtonByKey } = inject(BUTTONGROUP, {
  getButtonByKey: (val) => (Array.isArray(val) ? val : []),
})
const tableTopButtons = getButtonByKey(props.queryTabsButton)
const queryTabList = computed(() => {
  return (props.queryTabs ?? []).map((tab) => {
    if (tab.parameter?.options) {
      tab.parameter.options = tab.parameter.options.filter((item) => {
        if (item.alias) {
          return tableTopButtons.value.some((button) => button.code === item.alias)
        } else {
          return true
        }
      })
    }
    return tab
  })
})
const getDefaultQuert = (list) => {
  for (const queryTab of list) {
    for (const option of queryTab?.parameter?.options) {
      if (option.default) {
        const temp = {}
        setValueByPath(temp, queryTab.property, option.value)
        queryData.value = temp
        return
      }
    }
  }
}
getDefaultQuert(props.queryTabs ?? [])
//当配置了url时异步获取表格数据
const getDataAsyn = (pageNo) => {
  // loading.value = true
  onSelections(props.extendCompileData.property, [], props.rowConfig?.keyField ?? '_ID')
  return pgTable.value?.loadDataAsync(async (page, seachValue) => {
    page.pageNo = pageNo ?? page.pageNo
    const param = {
      pageNo: page.pageNo,
      pageSize: page.pageSize,
      tableCode: tableCode.value,
      ...props.dataParameters,
      sortings: sortings.value,
    }
    let requestUrl = props.url
    setValueByPath(param, 'parameters.keyword', seachValue)
    if (queryData.value) {
      const { _out, ...otherPram } = queryData.value
      if (_out) {
        Object.assign(param, _out)
      }
      if (props.queryTabs?.length > 0 && otherPram) {
        param.parameters = Object.assign(param.parameters, otherPram)
        const transformTab = props.queryTabs.find((item) => item.transform)
        const transformValue = transformTab ? otherPram[transformTab.property] : null
        if (transformTab && transformValue && typeof transformValue === 'object') {
          requestUrl = transformValue.url ?? props.url
          tableCode.value = transformValue.tableCode ?? tableCode.value
          param.parameters[transformTab.property] = transformValue.value
        } else {
          requestUrl = props.url
          tableCode.value = props.columnTableCode
        }
        if (tableCode.value !== param.tableCode) {
          await reloadColumn()
          param.sortings = sortings.value
        }
      }
    }
    onSeachParameters(props.extendCompileData.property, param)
    const [resData] = await post(requestUrl, param)
    loading.value = false
    if (!resData) return { data: [] }
    if (props.resDataKey) {
      resData.data = getValueByPath(resData, props.resDataKey) ?? []
    }
    resData.data = pgTable.value.creatRowId(resData.data ?? [])
    totalData.value = resData.total ?? null
    setData(props.extendCompileData.property, resData.data, props.rowConfig?.keyField ?? '_ID')
    return resData
  })
}
//异步获取表格数据时搜索使用当前方法否则使用后内置搜索方法
const seachMethod = props.url && !props.resDataKey ? () => getDataAsyn(1) : null
onMounted(() => {
  if (!init) return
  init(props.extendCompileData.property, {
    loadData: async (data, isRequest = true) => {
      // isRequest 当配置url时支持手动修改表格数据，不请求接口
      if (columns.value.length === 0) {
        await getcolumns()
      }
      if (isRequest && props.url) {
        getDataAsyn(1)
      } else {
        pgTable.value?.loadData(data, true, true)
        setData(props.extendCompileData.property, data, props.rowConfig?.keyField ?? '_ID')
        onSelections(props.extendCompileData.property, [], props.rowConfig?.keyField ?? '_ID')
      }
    },
    fullValid: () => {
      return pgTable.value?.fullValid?.()
    },
  })
  if (props.initLoad) {
    loadTable(props.extendCompileData.property)
  }
})

const sortChangeFn = ({ sortList }) => {
  // 排序后 重新请求数据
  if (props.url) {
    sortChange(sortList)
    getDataAsyn(1)
  }
}
const columnCustom = async () => {
  await reloadColumn()
  if (props.url) {
    getDataAsyn(1)
  }
}

const checkboxChange = ({ records }) => {
  onSelections(props.extendCompileData.property, records, props.rowConfig?.keyField ?? '_ID')
}
const checkboxAll = ({ records }) => {
  onSelections(props.extendCompileData.property, records, props.rowConfig?.keyField ?? '_ID')
}

const getNodeByClassName = (list, name) => {
  if (!list) return
  for (const node of list) {
    if (node.className.includes(name)) {
      return node
    }
  }
}

const getchildNode = (node, childClassName) => {
  for (const child of childClassName) {
    node = getNodeByClassName(node?.children, child)
  }
  return node
}

const { onPageEditScroll } = inject(PAGE_EDIT_SCROLL_INJECTION_KEY, {})
if (props.isFloat && onPageEditScroll) {
  let tableFooterFixedSpaceNode = null
  let tableFooterFixedRightSpaceNode = null

  onPageEditScroll((event) => {
    if (!pgTable.value) {
      return
    }

    const fixedElements = props.fixedElement.map((im) => document.querySelector(im))
    const otherFixedHeight = fixedElements.reduce((pre, cur) => pre + cur?.clientHeight, 0)

    //表格顶部按钮dom
    const tableButtonNode = getNodeByClassName(pgTable.value.$el.children, 'pg-table__button')
    //表格顶部按按钮浮动时的占位元素
    const tableButtonSpaceNode = getNodeByClassName(
      pgTable.value.$el.children,
      `pg-table-button-space${props.extendCompileData.property ?? ''}`
    )

    //表格dom
    const tableNode = getNodeByClassName(pgTable.value.$el.children, 'pg-table__table')
    //表格header dom
    const tableHeaderNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--main-wrapper',
      'vxe-table--header-wrapper',
    ])
    //表格header浮动时的占位元素
    const tableHeaderSpaceNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--main-wrapper',
      `pg-table-header-space${props.extendCompileData.property ?? ''}`,
    ])
    const tableHeaderFixedNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-left-wrapper',
      'vxe-table--header-wrapper',
    ])
    //表格header浮动时的占位元素
    const tableHeaderFixedSpaceNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-left-wrapper',
      `pg-table-header-fixed-left-space${props.extendCompileData.property ?? ''}`,
    ])
    const tableHeaderFixedRightNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-right-wrapper',
      'vxe-table--header-wrapper',
    ])
    //表格header浮动时的占位元素
    const tableHeaderFixedRightSpaceNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-right-wrapper',
      `pg-table-header-fixed-right-space${props.extendCompileData.property ?? ''}`,
    ])
    //表格底部距离顶部的距离
    const tableBottomOffsetTop = tableNode.offsetTop + tableNode.clientHeight
    //表格底部和表格header的高度
    const headerAndButtonHeight = tableButtonNode.clientHeight + tableHeaderNode.clientHeight
    //表格合计dom
    const tableFooterNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--main-wrapper',
      'vxe-table--footer-wrapper',
    ])
    const tableFooterFixedNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-left-wrapper',
      'vxe-table--footer-wrapper',
    ])
    const tableFooterFixedRightNode = getchildNode(tableNode, [
      'vxe-table',
      'vxe-table--render-wrapper',
      'vxe-table--fixed-wrapper',
      'vxe-table--fixed-right-wrapper',
      'vxe-table--footer-wrapper',
    ])
    //表格顶部按钮和表格header浮动的条件
    const isFixed =
      event.target.scrollTop > pgTable.value.$el.offsetTop - otherFixedHeight &&
      event.target.scrollTop <
        tableBottomOffsetTop -
          headerAndButtonHeight -
          (tableFooterNode?.clientHeight ?? 0) -
          otherFixedHeight
    if (!tableButtonSpaceNode && isFixed) {
      //创建表格顶部按钮占位元素
      const tableButtonNodeSpaceNew = document.createElement('div')
      tableButtonNodeSpaceNew.className = `pg-table-button-space${
        props.extendCompileData.property ?? ''
      }`
      tableButtonNodeSpaceNew.style.height = `${tableButtonNode.clientHeight}px`
      tableButtonNode.before(tableButtonNodeSpaceNew)

      //将表格顶部按钮浮动
      tableButtonNode.style.position = 'fixed'
      tableButtonNode.style.zIndex = 20
      tableButtonNode.style.top = `${getElementTop(event.target) + otherFixedHeight}px`
      tableButtonNode.style.width = `${pgTable.value.$el.clientWidth}px`
    } else if (tableButtonSpaceNode && !isFixed) {
      //表格顶部按钮取消浮动
      tableButtonNode.style.position = 'static'
      tableButtonNode.style.top = ''
      tableButtonNode.style.width = ''

      //删除表格顶部按钮占位元素
      tableButtonSpaceNode.remove()
    }

    if (!tableHeaderSpaceNode && isFixed) {
      //创建表格header占位元素
      const tableHeaderNodeSpaceNew = document.createElement('div')
      tableHeaderNodeSpaceNew.className = `pg-table-header-space${
        props.extendCompileData.property ?? ''
      }`
      tableHeaderNodeSpaceNew.style.height = `${tableHeaderNode.clientHeight}px`
      tableHeaderNode.before(tableHeaderNodeSpaceNew)

      //将表格header浮动
      tableHeaderNode.style.position = 'fixed'
      tableHeaderNode.style.zIndex = 5
      tableHeaderNode.style.top = `${
        getElementTop(event.target) + tableButtonNode.clientHeight + otherFixedHeight
      }px`
      tableHeaderNode.style.width = `${pgTable.value.$el.clientWidth}px`

      if (tableHeaderFixedNode) {
        //创建表格leftheader占位元素
        const tableHeaderFixedNodeSpaceNew = document.createElement('div')
        tableHeaderFixedNodeSpaceNew.className = `pg-table-header-fixed-left-space${
          props.extendCompileData.property ?? ''
        }`
        tableHeaderFixedNodeSpaceNew.style.height = `${tableHeaderFixedNode.clientHeight}px`
        tableHeaderFixedNode.before(tableHeaderFixedNodeSpaceNew)

        //将表格header浮动
        tableHeaderFixedNode.style.position = 'fixed'
        tableHeaderFixedNode.style.zIndex = 5
        tableHeaderFixedNode.style.top = `${
          getElementTop(event.target) + tableButtonNode.clientHeight + otherFixedHeight
        }px`
        tableHeaderFixedNode.style.left = `220px`
        tableHeaderFixedNode.style.width = `${tableHeaderFixedNode.clientWidth}px`
      }
      if (tableHeaderFixedRightNode) {
        //创建表格leftheader占位元素
        const tableHeaderFixedRightNodeSpaceNew = document.createElement('div')
        tableHeaderFixedRightNodeSpaceNew.className = `pg-table-header-fixed-right-space${
          props.extendCompileData.property ?? ''
        }`
        tableHeaderFixedRightNodeSpaceNew.style.height = `${tableHeaderFixedRightNode.clientHeight}px`
        tableHeaderFixedRightNode.before(tableHeaderFixedRightNodeSpaceNew)

        //将表格header浮动
        tableHeaderFixedRightNode.style.position = 'fixed'
        tableHeaderFixedRightNode.style.zIndex = 5
        tableHeaderFixedRightNode.style.top = `${
          getElementTop(event.target) + tableButtonNode.clientHeight + otherFixedHeight
        }px`
        tableHeaderFixedRightNode.style.right = `26px`
        tableHeaderFixedRightNode.style.width = `${tableHeaderFixedRightNode.clientWidth}px`
      }
    } else if (tableHeaderSpaceNode && !isFixed) {
      //表格顶部header取消浮动
      tableHeaderNode.style.position = 'relative'
      tableHeaderNode.style.zIndex = 'inherit'
      tableHeaderNode.style.top = ''
      tableHeaderNode.style.width = ''

      //删除表格顶部按钮占位元素
      tableHeaderSpaceNode.remove()

      if (tableHeaderFixedNode) {
        tableHeaderFixedNode.style.position = 'relative'
        tableHeaderFixedNode.style.zIndex = 'inherit'
        tableHeaderFixedNode.style.top = ''
        tableHeaderFixedNode.style.left = ''
        tableHeaderFixedNode.style.width = ''

        //删除表格顶部按钮占位元素
        tableHeaderFixedSpaceNode.remove()
      }
      if (tableHeaderFixedRightNode) {
        tableHeaderFixedRightNode.style.position = 'relative'
        tableHeaderFixedRightNode.style.zIndex = 'inherit'
        tableHeaderFixedRightNode.style.top = ''
        tableHeaderFixedRightNode.style.right = ''
        tableHeaderFixedRightNode.style.width = ''

        //删除表格顶部按钮占位元素
        tableHeaderFixedRightSpaceNode.remove()
      }
    }

    //分页浮动
    const pageNode = getNodeByClassName(pgTable.value.$el.children, 'pg-table__page')
    const pageSpaceNode = getNodeByClassName(
      pgTable.value.$el.children,
      `pg-table-space__page${props.extendCompileData.property ?? ''}`
    )
    //分页距离顶部的距离
    const pageNodeTop = pgTable.value.$el.offsetTop + pgTable.value.$el.clientHeight
    //当分页在可视区域以下并且表头在可视区域及以上时浮动合计栏
    const pageFixed =
      event.target.clientHeight < pageNodeTop - event.target.scrollTop &&
      event.target.clientHeight -
        headerAndButtonHeight -
        (tableFooterNode?.clientHeight ?? 0) -
        pageNode.clientHeight -
        otherFixedHeight >
        pgTable.value.$el.offsetTop - event.target.scrollTop
    if (!pageSpaceNode && pageFixed) {
      //创建表格合计占位元素
      const pageNodeSpaceNew = document.createElement('div')
      pageNodeSpaceNew.className = `pg-table-space__page${props.extendCompileData.property ?? ''}`
      pageNodeSpaceNew.style.height = `${pageNode.clientHeight}px`
      pageNode.before(pageNodeSpaceNew)

      //将表格header浮动
      pageNode.style.position = 'fixed'
      pageNode.style.zIndex = 20
      pageNode.style.top = `${
        getElementTop(event.target) + event.target.clientHeight - pageNode.clientHeight
      }px`
      pageNode.style.width = `${pgTable.value.$el.clientWidth}px`
    } else if (pageSpaceNode && !pageFixed) {
      //表格footer取消浮动
      pageNode.style.position = 'static'
      pageNode.style.top = ''
      pageNode.style.width = ''

      //删除表格顶部按钮占位元素
      pageSpaceNode.remove()
    }

    //合计栏浮动
    if (tableFooterNode) {
      //表格合计浮动时的占位元素
      const tableFooterSpaceNode = getchildNode(tableNode, [
        'vxe-table',
        'vxe-table--render-wrapper',
        'vxe-table--main-wrapper',
        `pg-table-footer-space${props.extendCompileData.property ?? ''}`,
      ])
      //合计记录顶部的距离
      const tableFooterNodeTop =
        pgTable.value.$el.offsetTop + pgTable.value.$el.clientHeight - tableFooterNode.clientHeight
      //当合计在可视区域以下并且表头在可视区域及以上时浮动合计栏
      const footerFixed =
        event.target.clientHeight - pageNode.clientHeight <
          tableFooterNodeTop - event.target.scrollTop &&
        event.target.clientHeight -
          headerAndButtonHeight -
          tableFooterNode.clientHeight -
          pageNode.clientHeight -
          otherFixedHeight >
          pgTable.value.$el.offsetTop - event.target.scrollTop
      if (!tableFooterSpaceNode && footerFixed) {
        //创建表格合计占位元素
        const tableFooterNodeSpaceNew = document.createElement('div')
        tableFooterNodeSpaceNew.className = `pg-table-footer-space${
          props.extendCompileData.property ?? ''
        }`
        tableFooterNodeSpaceNew.style.height = `${tableFooterNode.clientHeight}px`
        tableFooterNode.before(tableFooterNodeSpaceNew)

        //将表格header浮动
        tableFooterNode.style.position = 'fixed'
        tableFooterNode.style.zIndex = 6
        tableFooterNode.style.top = `${
          getElementTop(event.target) +
          event.target.clientHeight -
          tableFooterNode.clientHeight -
          pageNode.clientHeight +
          1
        }px`
        tableFooterNode.style.width = `${pgTable.value.$el.clientWidth}px`
        if (tableFooterFixedNode) {
          tableFooterFixedSpaceNode = tableFooterFixedNode.cloneNode(true)
          // tableFooterNodeFixedSpaceNew.style.height = `${tableFooterFixedNode.clientHeight}px`
          // tableFooterNodeFixedSpaceNew.style.top = `${tableFooterFixedNode.style.top}px`
          // tableFooterNodeFixedSpaceNew.style.marginTop = `-13px`

          tableFooterFixedNode.style.dispay = 'none'
          //将表格header浮动
          tableFooterFixedSpaceNode.style.position = 'fixed'
          tableFooterFixedSpaceNode.style.zIndex = 7
          const temTop = `${
            getElementTop(event.target) +
            event.target.clientHeight -
            tableFooterNode.clientHeight -
            pageNode.clientHeight +
            1
          }px`
          tableFooterFixedSpaceNode.style.top = temTop
          tableFooterFixedSpaceNode.style.left = '220px'
          tableFooterFixedSpaceNode.style.width = `${tableHeaderFixedNode.clientWidth}px`
          tableFooterFixedSpaceNode.style.height = `${tableFooterFixedNode.clientHeight}px`
          tableFooterFixedNode.parentNode.before(tableFooterFixedSpaceNode)
        }
        if (tableFooterFixedRightNode) {
          tableFooterFixedRightSpaceNode = tableFooterFixedRightNode.cloneNode(true)

          tableFooterFixedRightNode.style.dispay = 'none'
          //将表格header浮动
          tableFooterFixedRightSpaceNode.style.position = 'fixed'
          tableFooterFixedRightSpaceNode.style.zIndex = 7
          const temTop = `${
            getElementTop(event.target) +
            event.target.clientHeight -
            tableFooterNode.clientHeight -
            pageNode.clientHeight +
            1
          }px`
          tableFooterFixedRightSpaceNode.style.top = temTop
          tableFooterFixedRightSpaceNode.style.right = '26px'
          tableFooterFixedRightSpaceNode.style.width = `${tableHeaderFixedRightNode.clientWidth}px`
          tableFooterFixedRightSpaceNode.style.overflow = `hidden`
          tableFooterFixedRightSpaceNode.style.height = `${tableFooterFixedRightNode.clientHeight}px`
          tableFooterFixedRightNode.parentNode.before(tableFooterFixedRightSpaceNode)
        }
      } else if (tableFooterSpaceNode && !footerFixed) {
        //表格footer取消浮动
        tableFooterNode.style.position = 'relative'
        tableFooterNode.style.top = ''
        tableFooterNode.style.width = ''

        //删除表格顶部按钮占位元素
        tableFooterSpaceNode.remove()
        if (tableFooterFixedNode) {
          tableFooterFixedNode.style.dispay = 'block'
          //删除表格顶部按钮占位元素
          tableFooterFixedSpaceNode?.remove?.()
        }
        if (tableFooterFixedRightNode) {
          tableFooterFixedRightNode.style.dispay = 'block'
          //删除表格顶部按钮占位元素
          tableFooterFixedRightSpaceNode?.remove?.()
        }
      }
    }
  })
}
</script>
<style lang="scss">
@import '../style/page-edit-table.scss';
</style>
